/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import { DialogueMessage } from "@ictlearningsciences/pal3-core/types";
import {
  TopicAttributeRelevance,
  SurveyComparisonType,
  SurveyCondition,
  Survey,
  SurveyDelta,
  PlanDialogueMsgType,
  SurveyDialogueValidation,
} from "@ictlearningsciences/pal3-core/types";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { updateSurveyVariables } from "../store/slices/userData";

export interface UseWithSurveys {
  saveSurvey: (
    variables: TopicAttributeRelevance[],
    currentQuestion: number,
    goal?: string
  ) => void;
  getNextQuestionNumber: (
    survey: Survey,
    curQuestion: number,
    variables: TopicAttributeRelevance[]
  ) => number;
  evaluatePreconditions: (
    preconditions: SurveyCondition[],
    variables: TopicAttributeRelevance[]
  ) => boolean;
  validateText: (
    text: string,
    validation: SurveyDialogueValidation | undefined
  ) => Validation;
}

export interface Validation {
  type: PlanDialogueMsgType;
  valid: boolean;
  message: DialogueMessage;
}

export function updateUserVariables(
  userVariables: TopicAttributeRelevance[],
  deltas: SurveyDelta[],
  prevDeltas: SurveyDelta[]
): TopicAttributeRelevance[] {
  const updatedVariables: TopicAttributeRelevance[] = userVariables.map(
    (variable) => ({
      ...variable,
    })
  ); // Create copies of userVariables
  prevDeltas.forEach((prevDelta) => {
    const currentVariable = updatedVariables.find(
      (element) => element.name === prevDelta.variableName
    );
    if (currentVariable) {
      currentVariable.value -= prevDelta.delta;
    }
  });

  deltas.forEach((delta) => {
    const currentVariable = updatedVariables.find(
      (element) => element.name === delta.variableName
    );
    if (!currentVariable) {
      updatedVariables.push({ name: delta.variableName, value: delta.delta });
    } else {
      currentVariable.value += delta.delta;
    }
  });

  return updatedVariables;
}

export function useWithSurveys(): UseWithSurveys {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.userData);

  function getNextQuestionNumber(
    survey: Survey,
    curQuestion: number,
    variables: TopicAttributeRelevance[]
  ): number {
    const questions = survey.questions;
    for (let ii = curQuestion + 1; ii < questions.length; ii++) {
      if (evaluatePreconditions(questions[ii].preconditions, variables)) {
        return ii;
      }
    }
    return curQuestion;
  }

  function evaluatePreconditions(
    preconditions: SurveyCondition[],
    variables: TopicAttributeRelevance[]
  ): boolean {
    let result = true;
    preconditions?.forEach((element) => {
      const userValue =
        variables.find((e) => e.name === element.variable)?.value ||
        user?.traits.find((e) => e.name === element.variable)?.value ||
        0;
      if (element.operator == SurveyComparisonType.EQ)
        result = result && userValue == element.value;
      else if (element.operator == SurveyComparisonType.GTE)
        result = result && userValue >= element.value;
      else if (element.operator == SurveyComparisonType.LTE)
        result = result && userValue <= element.value;
    });
    return result;
  }

  function saveSurvey(
    variables: TopicAttributeRelevance[],
    currentQuestion: number,
    goal?: string
  ) {
    dispatch(
      updateSurveyVariables({
        goal: goal,
        data: variables,
        currentQuestion: currentQuestion,
      })
    );
  }

  function validateText(
    text: string,
    validation: SurveyDialogueValidation | undefined
  ): Validation {
    if (!validation) {
      return {
        valid: true,
        type: PlanDialogueMsgType.FeedbackNeutral,
        message: { text: "" },
      };
    }
    const wordCount = text?.split(" ").length;
    const charCount = text.replace(/\s/g, "").length;
    const minWordCount = validation.minWordCount || 0;
    const minCharCount = validation.minCharacterCount || 0;
    const recWordCount = validation.wordCount || 0;
    const recCharCount = validation.characterCount || 0;
    if (text.length > 1000) {
      return {
        valid: false,
        type: PlanDialogueMsgType.FeedbackNegative,
        message: {
          text: "Please keep your answer shorter than 1000 characters",
        },
      };
    }
    if (wordCount < minWordCount || charCount < minCharCount) {
      return {
        valid: false,
        type: PlanDialogueMsgType.FeedbackNegative,
        message: { text: "Can you explain that in more detail?" },
      };
    }
    if (validation.requiredRegex && !text.match(validation.requiredRegex)) {
      return {
        valid: false,
        type: PlanDialogueMsgType.FeedbackNegative,
        message: validation.feedback?.negative || {
          text: "The response you gave is invalid.",
        },
      };
    }
    if (
      wordCount >= recWordCount &&
      charCount >= recCharCount &&
      (!validation.regex || text.match(validation.regex))
    ) {
      return {
        valid: true,
        type: PlanDialogueMsgType.FeedbackPositive,
        message: validation.feedback?.positive || {
          text:
            recWordCount === 0 && recCharCount === 0
              ? ""
              : "Thanks, that is good to know.",
        },
      };
    }
    return {
      valid: true,
      type: PlanDialogueMsgType.FeedbackNeutral,
      message: { text: "" },
    };
  }

  return {
    saveSurvey,
    getNextQuestionNumber,
    evaluatePreconditions,
    validateText,
  };
}
